import AlbumGrid from '@/components/AlbumGrid';
import ContentSection from '@/components/ContentSection';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useClientMount } from '@/hooks/useClientMount';
import { api } from '@/lib/apiClient';
import { Album } from '@/types/interface';
import { useEffect, useMemo, useState } from 'react';

const Albums = () => {
    const [allAlbums, setAllAlbums] = useState<Album[]>([]); // Store all albums for filtering
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<'name' | 'id'>('name'); // Sort by name or id (as time proxy)
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const hasMounted = useClientMount();

    // Filter and sort albums based on search and sort criteria
    const filteredAndSortedAlbums = useMemo(() => {
        let filtered = allAlbums;

        // Filter by search query
        if (searchQuery.trim()) {
            filtered = allAlbums.filter(album => 
                album.title.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
                album.description?.toLowerCase().includes(searchQuery.toLowerCase().trim())
            );
        }

        // Sort albums
        const sorted = [...filtered].sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            if (sortBy === 'name') {
                aValue = a.title.toLowerCase();
                bValue = b.title.toLowerCase();
            } else {
                aValue = a.id;
                bValue = b.id;
            }

            if (sortOrder === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });

        return sorted;
    }, [allAlbums, searchQuery, sortBy, sortOrder]);

    // Paginate the filtered and sorted albums
    const itemsPerPage = 12;
    const paginatedAlbums = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredAndSortedAlbums.slice(startIndex, endIndex);
    }, [filteredAndSortedAlbums, currentPage]);

    // Calculate total pages for filtered results
    const calculatedTotalPages = Math.ceil(filteredAndSortedAlbums.length / itemsPerPage);

    // Fetch all albums
    useEffect(() => {
        if (!hasMounted) return;

        const fetchAlbums = async () => {
            try {
                // Fetch all albums (we'll handle pagination on frontend)
                const response = await api.albums.getAll({ page: 1, limit: 1000 }); // Large limit to get all albums
                const data = response.data;
                setAllAlbums(data.albums);
            } catch (err) {
                console.error('Error fetching albums:', err);
            }
        };

        fetchAlbums();
    }, [hasMounted]);

    // Reset to first page when search or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, sortBy, sortOrder]);

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Don't render anything until the component has mounted on the client
    if (!hasMounted) {
        return null;
    }

    return (
        <Layout>
            <div className="min-h-screen">
                <PageHeader title="Albums" />

                <ContentSection maxWidth="7xl" padding="xl">
                    {/* Search and Sort Controls */}
                    <div className="mb-12 space-y-4">
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center justify-between">
                            {/* Search Input */}
                            <div className="w-full sm:w-96">
                                <Input
                                    type="text"
                                    placeholder="Search albums..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full"
                                />
                            </div>

                            {/* Sort Controls */}
                            <div className="flex gap-2 items-center">
                                <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
                                <Button
                                    variant={sortBy === 'name' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSortBy('name')}
                                >
                                    Name
                                </Button>
                                <Button
                                    variant={sortBy === 'id' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSortBy('id')}
                                >
                                    Date
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                    className="min-w-[60px]"
                                >
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                </Button>
                            </div>
                        </div>

                        {/* Results count */}
                        {searchQuery.trim() && (
                            <p className="text-sm text-gray-500">
                                Found {filteredAndSortedAlbums.length} album{filteredAndSortedAlbums.length !== 1 ? 's' : ''}
                                {searchQuery.trim() && ` for "${searchQuery.trim()}"`}
                            </p>
                        )}
                    </div>

                    {paginatedAlbums.length > 0 ? (
                        <>
                            <AlbumGrid albums={paginatedAlbums} />

                            {/* Pagination */}
                            {calculatedTotalPages > 1 && (
                                <div className="mt-20 pt-8 border-t border-gray-100">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={calculatedTotalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-16">
                            <p className="text-gray-500 text-lg">
                                {searchQuery.trim() ? `No albums found for "${searchQuery.trim()}"` : 'No albums found'}
                            </p>
                            {searchQuery.trim() && (
                                <Button
                                    variant="ghost"
                                    onClick={() => setSearchQuery('')}
                                    className="mt-4"
                                >
                                    Clear search
                                </Button>
                            )}
                        </div>
                    )}
                </ContentSection>
            </div>
        </Layout>
    );
};

export default Albums;
